import {
  SW_ENABLED_EVENT,
  isServiceWorkerEnabled,
} from '@/services/swRegistration';
import * as React from 'react';
import {
  SyncStatus,
  getSyncStatus,
  isSyncStatusMessage,
  onSwMessage,
} from './swBridge';

/**
 * Whether the offline SW is installed on this device (opt-in). Live-updates
 * on install/uninstall via the SW_ENABLED_EVENT window event.
 */
export function useServiceWorkerEnabled(): boolean {
  const [enabled, setEnabled] = React.useState(isServiceWorkerEnabled);

  React.useEffect(() => {
    const update = () => setEnabled(isServiceWorkerEnabled());
    window.addEventListener(SW_ENABLED_EVENT, update);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener(SW_ENABLED_EVENT, update);
      window.removeEventListener('storage', update);
    };
  }, []);

  return enabled;
}

/** Latest sync status: seeded via GET_SYNC_STATUS, live via broadcasts. */
export function useSyncStatus(): SyncStatus | undefined {
  const [status, setStatus] = React.useState<SyncStatus>();

  React.useEffect(() => {
    let mounted = true;
    void getSyncStatus().then((initial) => {
      if (mounted && initial) {
        setStatus((current) => current ?? initial);
      }
    });
    const unsubscribe = onSwMessage((data) => {
      if (isSyncStatusMessage(data)) {
        setStatus(data.payload);
      }
    });
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  return status;
}

/**
 * Connectivity indicator: browser online/offline events, corrected by sync
 * outcomes (a sync that reaches the server proves online; a failed ping
 * proves offline regardless of what navigator.onLine claims).
 */
export function useIsOnline(): boolean {
  const [online, setOnline] = React.useState(() => navigator.onLine);

  React.useEffect(() => {
    const setFromNavigator = () => setOnline(navigator.onLine);
    window.addEventListener('online', setFromNavigator);
    window.addEventListener('offline', setFromNavigator);
    const unsubscribe = onSwMessage((data) => {
      if (isSyncStatusMessage(data)) {
        const { state } = data.payload;
        if (state === 'offline') {
          setOnline(false);
        } else if (state !== 'idle' && state !== 'aborted') {
          // syncing/succeeded/failed/auth-required all passed the ping —
          // they prove the server is reachable.
          setOnline(true);
        }
      }
    });
    return () => {
      window.removeEventListener('online', setFromNavigator);
      window.removeEventListener('offline', setFromNavigator);
      unsubscribe();
    };
  }, []);

  return online;
}
